import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isFeaturesDropdownOpen, setIsFeaturesDropdownOpen] = useState(false);
  const [isMobileFeaturesOpen, setIsMobileFeaturesOpen] = useState(false);
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const featuresDropdownRef = useRef<HTMLDivElement>(null);
  const featuresTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();

  const subMenuItems = [
    { key: 'item1', id: 'case-1', slug: 'compressor' },
    { key: 'item2', id: 'case-2', slug: 'refrigeration' },
    { key: 'item3', id: 'case-3', slug: 'cooling' },
    { key: 'item4', id: 'case-4', slug: 'press' },
    { key: 'item5', id: 'case-5', slug: 'crusher' },
    { key: 'item6', id: 'case-6', slug: 'injection' },
    { key: 'item7', id: 'case-7', slug: 'production' },
    { key: 'item8', id: 'case-8', slug: 'water-treatment' },
    { key: 'item9', id: 'case-9', slug: 'welding' },
    { key: 'item10', id: 'case-10', slug: 'boiler' },
    { key: 'item11', id: 'case-11', slug: 'electrical' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setIsLanguageDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 모바일 메뉴 열릴 때 스크롤 방지
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const isActive = (path: string) => location.pathname === path;

  // 홈페이지가 아닌 경우 항상 흰색 배경 표시
  const isHomePage = location.pathname === '/';
  const shouldShowBackground = !isHomePage || isScrolled;

  // 모바일에서는 항상 배경 표시
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
  const showBackground = shouldShowBackground || isMobile;

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setIsLanguageDropdownOpen(false);
  };

  const catalogPdfUrl = 'https://drive.google.com/file/d/119GxBf810tGVgZ3T2jabovzTeuBuKeSc/view?usp=drive_link';
  const catalogDownloadUrl = 'https://drive.google.com/uc?export=download&id=119GxBf810tGVgZ3T2jabovzTeuBuKeSc';

  const handleCatalogClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsCatalogModalOpen(true);
    setIsMobileMenuOpen(false);
  };

  const handleViewCatalog = () => {
    window.open(catalogPdfUrl, '_blank');
    setIsCatalogModalOpen(false);
  };

  const handleDownloadCatalog = () => {
    window.open(catalogDownloadUrl, '_blank');
    setIsCatalogModalOpen(false);
  };

  const handleFeaturesMouseEnter = () => {
    if (featuresTimeoutRef.current) {
      clearTimeout(featuresTimeoutRef.current);
      featuresTimeoutRef.current = null;
    }
    setIsFeaturesDropdownOpen(true);
  };

  const handleFeaturesMouseLeave = () => {
    featuresTimeoutRef.current = setTimeout(() => {
      setIsFeaturesDropdownOpen(false);
    }, 200);
  };

  const handleSubMenuClick = (slug: string) => {
    setIsFeaturesDropdownOpen(false);
    setIsMobileMenuOpen(false);
    setIsMobileFeaturesOpen(false);
    navigate(`/test-results/${slug}`);
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showBackground ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 md:gap-4">
              <img 
                src="https://static.readdy.ai/image/346dbd6eb3e8ee3112117c3d157f2a9e/66fbbd713a6751da4ee4738358ce6af4.png" 
                alt="SEPIS Logo" 
                className="h-10 md:h-16 w-auto"
              />
              <span className="hidden sm:inline-block px-2 md:px-3 py-1 md:py-1.5 text-xs md:text-sm font-semibold bg-gradient-to-r from-green-500 to-green-600 text-white rounded-full whitespace-nowrap">
                GLOBAL ONLY ONE
              </span>
            </Link>

            <div className="hidden lg:flex items-center space-x-5">
              <Link 
                to="/" 
                className={`text-base font-medium transition-colors whitespace-nowrap ${
                  isActive('/') 
                    ? 'text-orange-500' 
                    : shouldShowBackground ? 'text-gray-700 hover:text-orange-500' : 'text-white hover:text-orange-300'
                }`}
              >
                {t('nav.home')}
              </Link>
              <Link 
                to="/about" 
                className={`text-base font-medium transition-colors whitespace-nowrap ${
                  isActive('/about') 
                    ? 'text-orange-500' 
                    : shouldShowBackground ? 'text-gray-700 hover:text-orange-500' : 'text-white hover:text-orange-300'
                }`}
              >
                {t('nav.about')}
              </Link>
              <Link 
                to="/products" 
                className={`text-base font-medium transition-colors whitespace-nowrap ${
                  isActive('/products') 
                    ? 'text-orange-500' 
                    : shouldShowBackground ? 'text-gray-700 hover:text-orange-500' : 'text-white hover:text-orange-300'
                }`}
              >
                {t('nav.products')}
              </Link>
              <Link 
                to="/technology" 
                className={`text-base font-medium transition-colors whitespace-nowrap ${
                  isActive('/technology') 
                    ? 'text-orange-500' 
                    : shouldShowBackground ? 'text-gray-700 hover:text-orange-500' : 'text-white hover:text-orange-300'
                }`}
              >
                {t('nav.technology')}
              </Link>

              {/* 테스트결과 with dropdown */}
              <div 
                className="relative"
                ref={featuresDropdownRef}
                onMouseEnter={handleFeaturesMouseEnter}
                onMouseLeave={handleFeaturesMouseLeave}
              >
                <button 
                  onClick={() => setIsFeaturesDropdownOpen(!isFeaturesDropdownOpen)}
                  className={`text-base font-medium transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer ${
                    location.pathname.startsWith('/test-results') || isActive('/features')
                      ? 'text-orange-500' 
                      : shouldShowBackground ? 'text-gray-700 hover:text-orange-500' : 'text-white hover:text-orange-300'
                  }`}
                >
                  {t('nav.features')}
                  <i className={`ri-arrow-down-s-line text-sm transition-transform ${isFeaturesDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isFeaturesDropdownOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50">
                    <div className="bg-white rounded-xl shadow-2xl border border-gray-100 py-2 w-[280px] max-h-[480px] overflow-y-auto">
                      {subMenuItems.map((item, idx) => (
                        <button
                          key={item.key}
                          onClick={() => handleSubMenuClick(item.slug)}
                          className="w-full px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-500 transition-colors cursor-pointer flex items-center gap-2.5"
                        >
                          <span className="w-6 h-6 flex items-center justify-center rounded-full bg-orange-100 text-orange-500 text-xs font-bold flex-shrink-0">
                            {idx + 1}
                          </span>
                          <span className="truncate">{t(`nav.featuresSubMenu.${item.key}`)}</span>
                          <i className="ri-arrow-right-s-line text-xs ml-auto text-gray-400"></i>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link 
                to="/case-studies" 
                className={`text-base font-medium transition-colors whitespace-nowrap ${
                  isActive('/case-studies') 
                    ? 'text-orange-500' 
                    : shouldShowBackground ? 'text-gray-700 hover:text-orange-500' : 'text-white hover:text-orange-300'
                }`}
              >
                {t('nav.caseStudies')}
              </Link>

              <button 
                onClick={handleCatalogClick}
                className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  shouldShowBackground ? 'text-gray-700 hover:text-orange-500' : 'text-white hover:text-orange-300'
                }`}
              >
                {t('nav.catalog')}
              </button>
              <Link 
                to="/contact" 
                className={`text-base font-medium transition-colors whitespace-nowrap ${
                  isActive('/contact') 
                    ? 'text-orange-500' 
                    : shouldShowBackground ? 'text-gray-700 hover:text-orange-500' : 'text-white hover:text-orange-300'
                }`}
              >
                {t('nav.contact')}
              </Link>
              
              {/* Language Dropdown */}
              <div className="relative ml-3 pl-3 border-l border-gray-300" ref={languageDropdownRef}>
                <button 
                  onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                  className={`flex items-center gap-1.5 text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    shouldShowBackground ? 'text-gray-700 hover:text-orange-500' : 'text-white hover:text-orange-300'
                  }`}
                >
                  <i className="ri-global-line text-base"></i>
                  <span className="hidden xl:inline">Language</span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform ${isLanguageDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                
                {isLanguageDropdownOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-100 py-2 min-w-[140px] z-50">
                    <button
                      onClick={() => changeLanguage('ko')}
                      className={`w-full px-4 py-2.5 text-left text-sm font-medium transition-colors cursor-pointer flex items-center gap-2 ${
                        i18n.language === 'ko' ? 'text-orange-500 bg-orange-50' : 'text-gray-700 hover:bg-gray-50 hover:text-orange-500'
                      }`}
                    >
                      {i18n.language === 'ko' && <i className="ri-check-line text-orange-500"></i>}
                      <span className={i18n.language === 'ko' ? '' : 'ml-5'}>한글</span>
                    </button>
                    <button
                      onClick={() => changeLanguage('en')}
                      className={`w-full px-4 py-2.5 text-left text-sm font-medium transition-colors cursor-pointer flex items-center gap-2 ${
                        i18n.language === 'en' ? 'text-orange-500 bg-orange-50' : 'text-gray-700 hover:bg-gray-50 hover:text-orange-500'
                      }`}
                    >
                      {i18n.language === 'en' && <i className="ri-check-line text-orange-500"></i>}
                      <span className={i18n.language === 'en' ? '' : 'ml-5'}>English</span>
                    </button>
                    <button
                      onClick={() => changeLanguage('zh')}
                      className={`w-full px-4 py-2.5 text-left text-sm font-medium transition-colors cursor-pointer flex items-center gap-2 ${
                        i18n.language === 'zh' ? 'text-orange-500 bg-orange-50' : 'text-gray-700 hover:bg-gray-50 hover:text-orange-500'
                      }`}
                    >
                      {i18n.language === 'zh' && <i className="ri-check-line text-orange-500"></i>}
                      <span className={i18n.language === 'zh' ? '' : 'ml-5'}>中文</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 cursor-pointer"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-gray-900`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* 모바일 메뉴 오버레이 */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* 모바일 메뉴 */}
      <div className={`lg:hidden fixed top-0 right-0 h-full w-[280px] bg-white z-50 transform transition-transform duration-300 ease-in-out ${
        isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <span className="text-lg font-bold text-gray-900">메뉴</span>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 cursor-pointer"
            >
              <i className="ri-close-line text-2xl text-gray-700"></i>
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto py-4">
            <div className="flex flex-col space-y-1 px-4">
              <Link 
                to="/" 
                className={`text-base font-medium py-3 px-4 rounded-lg transition-colors ${
                  isActive('/') ? 'text-orange-500 bg-orange-50' : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="ri-home-line mr-3"></i>
                {t('nav.home')}
              </Link>
              <Link 
                to="/about" 
                className={`text-base font-medium py-3 px-4 rounded-lg transition-colors ${
                  isActive('/about') ? 'text-orange-500 bg-orange-50' : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="ri-information-line mr-3"></i>
                {t('nav.about')}
              </Link>
              <Link 
                to="/products" 
                className={`text-base font-medium py-3 px-4 rounded-lg transition-colors ${
                  isActive('/products') ? 'text-orange-500 bg-orange-50' : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="ri-box-3-line mr-3"></i>
                {t('nav.products')}
              </Link>
              <Link 
                to="/technology" 
                className={`text-base font-medium py-3 px-4 rounded-lg transition-colors ${
                  isActive('/technology') ? 'text-orange-500 bg-orange-50' : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="ri-cpu-line mr-3"></i>
                {t('nav.technology')}
              </Link>

              {/* 모바일 테스트결과 with accordion */}
              <div>
                <button
                  onClick={() => setIsMobileFeaturesOpen(!isMobileFeaturesOpen)}
                  className={`w-full text-base font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-between cursor-pointer ${
                    location.pathname.startsWith('/test-results') ? 'text-orange-500 bg-orange-50' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span>
                    <i className="ri-bar-chart-box-line mr-3"></i>
                    {t('nav.features')}
                  </span>
                  <i className={`ri-arrow-down-s-line transition-transform ${isMobileFeaturesOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {isMobileFeaturesOpen && (
                  <div className="ml-4 mt-1 space-y-0.5">
                    {subMenuItems.map((item, idx) => (
                      <button
                        key={item.key}
                        onClick={() => handleSubMenuClick(item.slug)}
                        className="w-full text-left text-sm font-medium py-2 px-4 rounded-lg text-gray-600 hover:bg-orange-50 hover:text-orange-500 transition-colors cursor-pointer flex items-center gap-2"
                      >
                        <span className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 text-xs font-bold flex-shrink-0">
                          {idx + 1}
                        </span>
                        <span className="truncate">{t(`nav.featuresSubMenu.${item.key}`)}</span>
                        <i className="ri-arrow-right-s-line text-xs ml-auto text-gray-400"></i>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <Link 
                to="/case-studies" 
                className={`text-base font-medium py-3 px-4 rounded-lg transition-colors ${
                  isActive('/case-studies') ? 'text-orange-500 bg-orange-50' : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="ri-file-list-2-line mr-3"></i>
                {t('nav.caseStudies')}
              </Link>

              <button 
                onClick={handleCatalogClick}
                className="text-base font-medium py-3 px-4 rounded-lg text-gray-700 hover:bg-gray-50 text-left cursor-pointer transition-colors"
              >
                <i className="ri-file-pdf-2-line mr-3"></i>
                {t('nav.catalog')}
              </button>
              <Link 
                to="/contact" 
                className={`text-base font-medium py-3 px-4 rounded-lg transition-colors ${
                  isActive('/contact') ? 'text-orange-500 bg-orange-50' : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="ri-customer-service-2-line mr-3"></i>
                {t('nav.contact')}
              </Link>
            </div>
            
            {/* Mobile Language Selection */}
            <div className="mt-6 pt-6 border-t border-gray-200 px-4">
              <p className="text-xs text-gray-500 mb-3 flex items-center gap-1 px-4">
                <i className="ri-global-line"></i>
                Language
              </p>
              <div className="flex flex-col gap-1">
                <button 
                  onClick={() => {
                    changeLanguage('ko');
                    setIsMobileMenuOpen(false);
                  }} 
                  className={`text-base font-medium py-3 px-4 rounded-lg transition-colors cursor-pointer text-left flex items-center gap-2 ${
                    i18n.language === 'ko' ? 'text-orange-500 bg-orange-50' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {i18n.language === 'ko' && <i className="ri-check-line text-orange-500"></i>}
                  <span className={i18n.language === 'ko' ? '' : 'ml-5'}>한글</span>
                </button>
                <button 
                  onClick={() => {
                    changeLanguage('en');
                    setIsMobileMenuOpen(false);
                  }} 
                  className={`text-base font-medium py-3 px-4 rounded-lg transition-colors cursor-pointer text-left flex items-center gap-2 ${
                    i18n.language === 'en' ? 'text-orange-500 bg-orange-50' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {i18n.language === 'en' && <i className="ri-check-line text-orange-500"></i>}
                  <span className={i18n.language === 'en' ? '' : 'ml-5'}>English</span>
                </button>
                <button 
                  onClick={() => {
                    changeLanguage('zh');
                    setIsMobileMenuOpen(false);
                  }} 
                  className={`text-base font-medium py-3 px-4 rounded-lg transition-colors cursor-pointer text-left flex items-center gap-2 ${
                    i18n.language === 'zh' ? 'text-orange-500 bg-orange-50' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {i18n.language === 'zh' && <i className="ri-check-line text-orange-500"></i>}
                  <span className={i18n.language === 'zh' ? '' : 'ml-5'}>中文</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 카탈로그 선택 모달 */}
      {isCatalogModalOpen && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setIsCatalogModalOpen(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-w-md w-full transform transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-pdf-2-line text-white text-2xl md:text-3xl"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{t('nav.catalog')}</h3>
              <p className="text-gray-600 text-sm">원하시는 방식을 선택해주세요</p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={handleViewCatalog}
                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-medium hover:from-orange-600 hover:to-orange-700 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-eye-line text-xl"></i>
                <span>바로 보기</span>
              </button>
              
              <button
                onClick={handleDownloadCatalog}
                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-2-line text-xl"></i>
                <span>PDF 다운로드</span>
              </button>
            </div>
            
            <button
              onClick={() => setIsCatalogModalOpen(false)}
              className="mt-6 w-full text-center text-gray-500 text-sm hover:text-gray-700 transition-colors cursor-pointer"
            >
              닫기
            </button>
          </div>
        </div>
      )}
    </>
  );
}
